import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Table, Icon, Label, Popup } from "semantic-ui-react";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { QRCodeSVG } from "qrcode.react";
import { Inspection, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany } from "../../../../contexts";
import { generateInspectionReportPdf } from "../inspectionReportPdf";
import "../ChecklistDashboard/ChecklistDashboard.scss";
import "./ChecklistInspectionsList.scss";

const inspectionCtrl = new Inspection();
const siteCtrl        = new Site();
const CURRENT_YEAR = new Date().getFullYear();
const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || window.location.origin;

const INSP_STATUS = {
  draft:        { label: "Borrador",      color: "#f9a825", bg: "#FFF8E1" },
  completed:    { label: "Completada",    color: "#27AE60", bg: "#E9F7EF" },
  with_actions: { label: "Con acciones",  color: "#1976D2", bg: "#E3F2FD" },
  closed:       { label: "Cerrado",       color: "#27AE60", bg: "#E9F7EF" },
  cancelled:    { label: "Anulada",       color: "#C0392B", bg: "#FDECEA" },
};

function semaphoreColor(pct) {
  if (pct >= 80) return "#27AE60";
  if (pct >= 60) return "#E67E22";
  return "#C0392B";
}

function shortId(id) {
  return `INSP-${(id || "").slice(-6).toUpperCase()}`;
}

// Mismo patrón que "Norma" en ChecklistExplore.js / "Estado"-"Sitio" en
// LegalRequirementsTable.js: al abrirlo despliega un menú propio con las
// opciones como pills blancas, no el dropdown nativo del navegador.
function StatusFilterDropdown({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // value:"" siempre muestra el placeholder "Estado" (no la opción "Todos"
  // con el mismo value vacío) — "Todos" solo se usa como ítem del menú para
  // volver a limpiar el filtro.
  const selected = value ? options.find((o) => o.value === value) : null;

  return (
    <div className="cli-fd" ref={ref}>
      <button type="button" className="cli-fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{selected ? selected.text : placeholder}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`cli-fd-arrow${open ? " cli-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7" />
        </svg>
      </button>
      {open && (
        <div className="cli-fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`cli-fd-item${o.value === value ? " cli-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Menú "⋮" por fila (Informe) — reemplaza los 3 botones sueltos (PDF/Excel/
// mail) por un solo trigger compacto, mismo patrón que .cld__export-menu de
// arriba (toggle + lista desplegable, click afuera cierra).
// Portal a document.body (mismo patrón que .lr-table__row-menu en
// LegalRequirementsTable.js) — el menú posicionado solo con absolute
// adentro de la celda quedaba tapado por las filas de abajo (cada td de la
// tabla es su propio rectángulo blanco con fondo sólido, ver .cld__tbl).
function InformeMenu({ onPdf, onExcel, onEmail }) {
  const [pos, setPos] = useState(null); // { top, left, openUp } | null
  const btnRef = useRef(null);

  useEffect(() => {
    if (!pos) return;
    const handler = (e) => {
      if (!e.target.closest(".cli__row-menu, .cli__row-menu-list")) setPos(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pos]);

  const toggle = () => {
    if (pos) { setPos(null); return; }
    const rect = btnRef.current.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setPos({
      left: rect.right - 180,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  return (
    <div className="cli__row-menu">
      <button type="button" className="cli__row-menu-btn" ref={btnRef} onClick={toggle} title="Informe">
        <Icon name="ellipsis vertical" />
      </button>
      {pos && ReactDOM.createPortal(
        <div
          className="cli__row-menu-list"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: pos.openUp ? "translateY(-100%)" : "none",
          }}
        >
          <button type="button" onClick={() => { onPdf(); setPos(null); }}>
            <Icon name="file pdf outline" />Descargar PDF
          </button>
          <button type="button" onClick={() => { onExcel(); setPos(null); }}>
            <Icon name="file excel outline" />Descargar Excel
          </button>
          <button type="button" onClick={() => { onEmail(); setPos(null); }}>
            <Icon name="mail outline" />Enviar por correo
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

function hallazgosInfo(insp) {
  const list  = insp.hallazgos || [];
  const total = list.length;
  const open  = list.filter((h) => h.status === "open").length;
  return { total, open };
}

function formatDate(d) {
  if (!d) return "-";
  const str = typeof d === "string" ? d : new Date(d).toISOString();
  const [y, m, day] = str.split("T")[0].split("-");
  return `${day}/${m}/${y}`;
}

export function ChecklistInspectionsList({ reload, onNew, onEdit, year = CURRENT_YEAR }) {
  const { accessToken } = useAuth();
  const { site: contextSite } = useSite();
  const { company } = useCompany();
  const navigate = useNavigate();

  const [inspections,    setInspections]    = useState([]);
  const [loadingInsp,    setLoadingInsp]    = useState(true);
  const [searchInsp,     setSearchInsp]     = useState("");
  const [filterStatus,   setFilterStatus]   = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [companySiteIds, setCompanySiteIds] = useState([]);
  const [qrTarget,       setQrTarget]       = useState(null);
  const exportMenuRef = useRef(null);

  const noSiteInCtx    = !contextSite || contextSite === "0" || contextSite === 0;
  const noCompanyInCtx = !company     || company     === "0" || company     === 0;

  // Sitios de la empresa activa (para no traer inspecciones de otras
  // empresas del mismo tenant cuando no hay un sitio puntual elegido).
  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, company, true)
      .then((res) => setCompanySiteIds((res || []).map((s) => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, company, noCompanyInCtx]);

  useEffect(() => {
    setLoadingInsp(true);
    const params = { year };
    if (!noSiteInCtx) {
      params.site = contextSite;
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      params.site = companySiteIds.join(",");
    }
    inspectionCtrl.getInspections(accessToken, params)
      .then((res) => setInspections(Array.isArray(res) ? res : []))
      .catch(() => setInspections([]))
      .finally(() => setLoadingInsp(false));
  }, [accessToken, year, contextSite, reload, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  const filteredInspections = useMemo(() => {
    return inspections.filter((i) => {
      if (filterStatus && i.status !== filterStatus) return false;
      if (searchInsp) {
        const q    = searchInsp.toLowerCase();
        const tpl  = (i.template?.name || "").toLowerCase();
        const site = (i.site?.name || "").toLowerCase();
        const insp = (i.signature?.name || [i.inspector?.firstname, i.inspector?.lastname].filter(Boolean).join(" ")).toLowerCase();
        if (!tpl.includes(q) && !site.includes(q) && !insp.includes(q)) return false;
      }
      return true;
    });
  }, [inspections, filterStatus, searchInsp]);

  const handleEditClick = async (insp) => {
    try {
      const tplId = insp.template?._id || insp.template;
      const tpl = await inspectionCtrl.getTemplate(accessToken, tplId);
      onEdit?.({ inspection: insp, template: tpl });
    } catch {
      toast.error("Error al cargar la plantilla para editar.");
    }
  };

  const exportRows = (rows) => rows.map((i) => ({
    "Checklist":    i.template?.name || "-",
    "Sitio":        i.site?.name || "-",
    "Área":         i.area?.name || "-",
    "Inspector":    i.signature?.name || [i.inspector?.firstname, i.inspector?.lastname].filter(Boolean).join(" ") || "-",
    "Completado por": [i.completedBy?.firstname, i.completedBy?.lastname].filter(Boolean).join(" ") || "-",
    "Fecha":        formatDate(i.date),
    "Estado":       INSP_STATUS[i.status]?.label || i.status || "-",
    "Cumplimiento": i.cumplimiento !== null && i.cumplimiento !== undefined ? `${i.cumplimiento.toFixed(1)}%` : "N/D",
  }));

  const handleExportCSV = () => {
    const rows = exportRows(filteredInspections);
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), `inspecciones_${year}.csv`);
  };

  const handleExportXLSX = () => {
    const rows = exportRows(filteredInspections);
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inspecciones");
    XLSX.writeFile(wb, `inspecciones_${year}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = exportRows(filteredInspections);
    if (!rows.length) return;
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(13);
    doc.text(`Inspecciones ${year}`, pageWidth / 2, 14, { align: "center" });
    const headers = Object.keys(rows[0]);
    autoTable(doc, {
      head: [headers],
      body: rows.map((r) => headers.map((h) => r[h])),
      startY: 20,
      styles: { fontSize: 8 },
    });
    doc.save(`inspecciones_${year}.pdf`);
  };

  // ── Export por fila (una sola inspección) ─────────────────────────────────
  const handleExportRowXLSX = (insp) => {
    const rows = exportRows([insp]);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inspección");
    XLSX.writeFile(wb, `${shortId(insp._id)}.xlsx`);
  };

  const handleExportRowPDF = async (insp) => {
    try {
      await generateInspectionReportPdf({ inspection: insp, accessToken, company });
    } catch {
      toast.error("Error al generar el informe PDF.");
    }
  };

  // mailto — sin adjunto (requiere backend de mail para enviarlo real); el
  // usuario adjunta a mano el PDF/Excel que acaba de exportar de esta fila.
  const handleEmailRow = (insp) => {
    const subject = encodeURIComponent(`Informe de inspección ${shortId(insp._id)} — ${insp.template?.name || ""}`);
    const body = encodeURIComponent(
      [
        `Checklist: ${insp.template?.name || "-"}`,
        `Fecha: ${formatDate(insp.date)}`,
        `Cumplimiento: ${insp.cumplimiento !== null && insp.cumplimiento !== undefined ? `${insp.cumplimiento.toFixed(1)}%` : "N/D"}`,
        `Estado: ${INSP_STATUS[insp.status]?.label || insp.status || "-"}`,
        "",
        "(Adjuntá el PDF o Excel exportado desde esta fila antes de enviar.)",
      ].join("\n")
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="cld">
      {/* Franja gris (Rectangle 201) — mismo contenedor que en Explorar,
          envuelve filtros + tabla y se ajusta al tamaño del contenido. */}
      <div className="cli__panel">
        {/* Filters — cajas individuales planas, mismo estilo que Explorar
            (buscador + norma), no una card única con ícono/label apilado.
            "Exportar" va pegado al borde derecho del todo, misma fila. */}
        <div className="cli__filters">
          <div className="cli__search-wrapper">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="cli__filter-box cli__filter-box--search"
              placeholder="Buscar"
              value={searchInsp}
              onChange={(e) => setSearchInsp(e.target.value)}
            />
          </div>
          <StatusFilterDropdown
            options={[
              { key: "all", value: "", text: "Todos" },
              ...Object.entries(INSP_STATUS).map(([val, { label }]) => ({ key: val, value: val, text: label })),
            ]}
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="Estado"
          />

          <div className="cld__export-dropdown" ref={exportMenuRef}>
            <button className="cld__export-toggle" onClick={() => setShowExportMenu((v) => !v)}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
                <path d="M3 13h10M8 3v7m0 0L5 7m3 3 3-3"/>
              </svg>
              Exportar
              <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
            </button>
            {showExportMenu && (
              <div className="cld__export-menu">
                <button className="cld__export-menu-item" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  CSV
                </button>
                <button className="cld__export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  Excel (XLSX)
                </button>
                <button className="cld__export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="cld__table-card">
          <div className="cld__wrapper">
            {loadingInsp ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#aaa" }}>Cargando…</div>
            ) : (
              <Table className="cld__tbl">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>ID</Table.HeaderCell>
                    <Table.HeaderCell>Fecha</Table.HeaderCell>
                    <Table.HeaderCell>Checklist</Table.HeaderCell>
                    <Table.HeaderCell>Inspector</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Cumpl.</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Hallazgos</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Estado</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Informe</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredInspections.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={8} textAlign="center" style={{ color: "#999" }}>
                        Sin inspecciones para mostrar
                      </Table.Cell>
                    </Table.Row>
                  ) : filteredInspections.map((insp) => {
                    const st = INSP_STATUS[insp.status] || { label: insp.status, color: "#888", bg: "#F3F4F6" };
                    const inspector = insp.signature?.name || [insp.inspector?.firstname, insp.inspector?.lastname].filter(Boolean).join(" ") || "-";
                    const completedBy = [insp.completedBy?.firstname, insp.completedBy?.lastname].filter(Boolean).join(" ");
                    const cumpl = insp.cumplimiento;
                    const { total: hallazgosTotal, open: hallazgosOpen } = hallazgosInfo(insp);
                    return (
                      <Table.Row key={insp._id}>
                        <Table.Cell data-label="ID">
                          <Popup size="mini" content="Inspeccionar desde el celular (QR)" trigger={
                            <button
                              type="button"
                              className="cli__id-pill cli__id-pill--qr"
                              onClick={() => setQrTarget(insp)}
                            >
                              <Icon name="qrcode" />
                              {shortId(insp._id)}
                            </button>
                          } />
                        </Table.Cell>
                        <Table.Cell data-label="Fecha">
                          <Icon name="calendar outline" />{formatDate(insp.date)}
                        </Table.Cell>
                        <Table.Cell data-label="Checklist" style={{ textAlign: "left" }}>
                          <div className="cld__title-cell">
                            <button
                              type="button"
                              className="cli__title-link"
                              onClick={() => handleEditClick(insp)}
                            >
                              {insp.template?.name || "-"}
                            </button>
                            {insp.template?.category && (
                              <span className="cld__title-sub">{insp.template.category}</span>
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell data-label="Inspector">
                          <div>{inspector}</div>
                          {completedBy && (
                            <div style={{ fontSize: "0.75rem", color: "#888" }}>
                              Completado por: {completedBy}
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell data-label="Cumpl." textAlign="center">
                          {cumpl !== null && cumpl !== undefined ? (
                            <div className="cli__cumpl">
                              <div className="cli__cumpl-bar">
                                <div
                                  className="cli__cumpl-bar-fill"
                                  style={{ width: `${cumpl}%`, background: semaphoreColor(cumpl) }}
                                />
                              </div>
                              <span style={{ color: semaphoreColor(cumpl) }}>{Math.round(cumpl)}%</span>
                            </div>
                          ) : (
                            <Label size="small" color="grey">N/D</Label>
                          )}
                        </Table.Cell>
                        <Table.Cell data-label="Hallazgos" textAlign="center">
                          {hallazgosTotal === 0 ? (
                            <span className="cli__pill cli__pill--gray">Sin hallazgos</span>
                          ) : hallazgosOpen > 0 ? (
                            <span className="cli__pill cli__pill--red">
                              {hallazgosTotal} · {hallazgosOpen} abierto{hallazgosOpen !== 1 ? "s" : ""}
                            </span>
                          ) : (
                            <span className="cli__pill cli__pill--green">{hallazgosTotal} · cerrado</span>
                          )}
                        </Table.Cell>
                        <Table.Cell data-label="Estado" textAlign="center">
                          <span className="cli__pill" style={{ background: st.bg, color: st.color }}>
                            {st.label}
                          </span>
                        </Table.Cell>
                        <Table.Cell textAlign="center" className="cli__informe-cell">
                          <InformeMenu
                            onPdf={() => handleExportRowPDF(insp)}
                            onExcel={() => handleExportRowXLSX(insp)}
                            onEmail={() => handleEmailRow(insp)}
                          />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            )}
          </div>
        </div>
      </div>

      {qrTarget && (
        <div className="cld__modal-overlay" onClick={() => setQrTarget(null)}>
          <div className="cld__modal" onClick={(e) => e.stopPropagation()}>
            <div className="cld__modal-header">
              <Icon name="qrcode" />
              Inspeccionar desde el celular
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0 1rem" }}>
              <QRCodeSVG value={`${CLIENT_URL}/inspect/${qrTarget._id}`} size={180} level="M" />
              <span style={{ fontSize: "0.8rem", color: "#888", textAlign: "center", wordBreak: "break-all" }}>
                {`${CLIENT_URL}/inspect/${qrTarget._id}`}
              </span>
            </div>
            <div className="cld__modal-footer">
              <button className="cld__text-btn" onClick={() => setQrTarget(null)}>
                Cerrar
              </button>
              <button
                className="cld__text-btn cld__text-btn--save"
                onClick={() => { const id = qrTarget._id; setQrTarget(null); navigate(`/inspect/${id}`); }}
              >
                <Icon name="mobile alternate" />Abrir aquí
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
